import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import {
  BarChart3,
  Download,
  Filter,
  MessageSquare,
  TrendingUp,
  Users,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
} from 'lucide-react';
import { formatDate } from '@/lib/formatters';

interface Company {
  id: string;
  name: string;
}

interface Operador {
  id: string;
  name: string;
  email: string;
}

interface CobrancasPorOperador {
  operadorId: string;
  operadorNome: string;
  operadorEmail: string;
  totalMensagens: number;
  mensagensEnviadas: number;
  mensagensEntregues: number;
  mensagensLidas: number;
  mensagensFalhadas: number;
  taxaSucesso: number;
  totalClientes: number;
  clientesUnicos: string[];
  periodo: {
    inicio: string;
    fim: string;
  };
}

interface CobrancasPorOperadorDetalhado extends CobrancasPorOperador {
  mensagens: Array<{
    id: string;
    clienteNome: string;
    clienteId: string;
    content: string;
    timestamp: string;
    status: string;
    messageType: string;
  }>;
}

export const WhatsAppCobrancasReportPage: React.FC = () => {
  // Estados para filtros
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [selectedOperador, setSelectedOperador] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [selectedOperadorDetalhado, setSelectedOperadorDetalhado] = useState<string | null>(null);

  // Estados para dados
  const [companies, setCompanies] = useState<Company[]>([]);
  const [operadores, setOperadores] = useState<Operador[]>([]);

  // Query para buscar relatório
  const { data: reportData, isLoading, refetch } = useQuery({
    queryKey: ['whatsapp-cobrancas-por-operador', selectedCompany, selectedOperador, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCompany) params.append('companyId', selectedCompany);
      if (selectedOperador) params.append('operadorId', selectedOperador);
      if (dateFrom) params.append('startDate', dateFrom);
      if (dateTo) params.append('endDate', dateTo);

      const response = await api.get(`/whatsapp-integration/reports/cobrancas-por-operador?${params.toString()}`);
      return response.data.data;
    },
  });

  // Query para relatório detalhado
  const { data: detalhadoData, isLoading: isLoadingDetalhado } = useQuery<CobrancasPorOperadorDetalhado | null>({
    queryKey: ['whatsapp-cobrancas-detalhado', selectedOperadorDetalhado, selectedCompany, dateFrom, dateTo],
    queryFn: async () => {
      if (!selectedOperadorDetalhado) return null;
      const queryParams = new URLSearchParams();
      if (selectedCompany) queryParams.append('companyId', selectedCompany);
      if (dateFrom) queryParams.append('startDate', dateFrom);
      if (dateTo) queryParams.append('endDate', dateTo);

      const response = await api.get(
        `/whatsapp-integration/reports/cobrancas-por-operador/${selectedOperadorDetalhado}?${queryParams.toString()}`
      );
      return response.data.data;
    },
    enabled: !!selectedOperadorDetalhado,
  });

  // Carregar empresas e operadores
  useEffect(() => {
    loadCompanies();
    loadOperadores();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await api.get('/companies?isActive=true');
      setCompanies(response.data.data.companies || []);
    } catch (error) {
      toast.error('Erro ao carregar empresas');
    }
  };

  const loadOperadores = async () => {
    try {
      const response = await api.get('/users?role=OPERADOR&isActive=true');
      setOperadores(response.data.data.users || []);
    } catch (error) {
      toast.error('Erro ao carregar operadores');
    }
  };

  const exportReport = async () => {
    try {
      // Criar CSV manualmente
      if (!reportData?.relatorio) {
        toast.error('Nenhum dado para exportar');
        return;
      }

      const csvRows = [
        ['Operador', 'Email', 'Total Mensagens', 'Enviadas', 'Entregues', 'Lidas', 'Falhadas', 'Taxa Sucesso (%)', 'Total Clientes'].join(','),
        ...reportData.relatorio.map((r: CobrancasPorOperador) =>
          [
            r.operadorNome,
            r.operadorEmail,
            r.totalMensagens,
            r.mensagensEnviadas,
            r.mensagensEntregues,
            r.mensagensLidas,
            r.mensagensFalhadas,
            r.taxaSucesso.toFixed(2),
            r.totalClientes,
          ].join(',')
        ),
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cobrancas-whatsapp-por-operador-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Relatório exportado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao exportar relatório:', error);
      toast.error('Erro ao exportar relatório');
    }
  };

  const clearFilters = () => {
    setSelectedCompany('');
    setSelectedOperador('');
    const date = new Date();
    date.setDate(date.getDate() - 30);
    setDateFrom(date.toISOString().split('T')[0]);
    setDateTo(new Date().toISOString().split('T')[0]);
    setSelectedOperadorDetalhado(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'delivered':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'read':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="page-whatsapp-cobrancas-report">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Relatório de Cobranças por Operador</h1>
          <p className="text-muted-foreground">
            Estatísticas de envio de mensagens de cobrança via WhatsApp agrupadas por operador
          </p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={() => refetch()} variant="outline" disabled={isLoading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={exportReport} disabled={isLoading || !reportData}>
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Empresa */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Empresa
              </label>
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <option value="">Todas as empresas</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Operador */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Operador
              </label>
              <Select value={selectedOperador} onValueChange={setSelectedOperador}>
                <option value="">Todos os operadores</option>
                {operadores.map((operador) => (
                  <option key={operador.id} value={operador.id}>
                    {operador.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Data Inicial */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Data Inicial
              </label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            {/* Data Final */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Data Final
              </label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button onClick={clearFilters} variant="outline">
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Estatísticas Gerais */}
      {reportData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Users className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{reportData.totalOperadores}</p>
                  <p className="text-sm text-muted-foreground">Operadores</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <MessageSquare className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{reportData.totalMensagens}</p>
                  <p className="text-sm text-muted-foreground">Total de Mensagens</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-8 w-8 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold">
                    {reportData.relatorio.length > 0
                      ? (
                          reportData.relatorio.reduce(
                            (sum: number, r: CobrancasPorOperador) => sum + r.taxaSucesso,
                            0
                          ) / reportData.relatorio.length
                        ).toFixed(1)
                      : 0}
                    %
                  </p>
                  <p className="text-sm text-muted-foreground">Taxa Média de Sucesso</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-8 w-8 text-orange-600" />
                <div>
                  <p className="text-2xl font-bold">
                    {reportData.relatorio.reduce(
                      (sum: number, r: CobrancasPorOperador) => sum + r.totalClientes,
                      0
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">Total de Clientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabela de Operadores */}
      <Card>
        <CardHeader>
          <CardTitle>Estatísticas por Operador</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : reportData && reportData.relatorio.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium">Operador</th>
                    <th className="text-left py-3 px-4 font-medium">Total Mensagens</th>
                    <th className="text-left py-3 px-4 font-medium">Enviadas</th>
                    <th className="text-left py-3 px-4 font-medium">Entregues</th>
                    <th className="text-left py-3 px-4 font-medium">Lidas</th>
                    <th className="text-left py-3 px-4 font-medium">Falhas</th>
                    <th className="text-left py-3 px-4 font-medium">Taxa Sucesso</th>
                    <th className="text-left py-3 px-4 font-medium">Clientes</th>
                    <th className="text-left py-3 px-4 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.relatorio.map((operador: CobrancasPorOperador) => (
                    <tr
                      key={operador.operadorId}
                      className="border-b border-gray-100 dark:border-slate-700 dark:border-slate-700 hover:bg-muted/50 dark:hover:bg-slate-700"
                    >
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">{operador.operadorNome}</p>
                          <p className="text-xs text-muted-foreground">{operador.operadorEmail}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-sm font-semibold">
                          {operador.totalMensagens}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-blue-500" />
                          <span>{operador.mensagensEnviadas}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>{operador.mensagensEntregues}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-purple-500" />
                          <span>{operador.mensagensLidas}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span>{operador.mensagensFalhadas}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge
                          className={
                            operador.taxaSucesso >= 90
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : operador.taxaSucesso >= 70
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }
                        >
                          {operador.taxaSucesso.toFixed(1)}%
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline">{operador.totalClientes}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setSelectedOperadorDetalhado(
                              selectedOperadorDetalhado === operador.operadorId
                                ? null
                                : operador.operadorId
                            )
                          }
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          {selectedOperadorDetalhado === operador.operadorId ? 'Ocultar' : 'Detalhes'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhum dado encontrado</p>
              <p className="text-sm">Ajuste os filtros para ver estatísticas</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detalhes do Operador Selecionado */}
      {selectedOperadorDetalhado && detalhadoData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Detalhes - {detalhadoData.operadorNome}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedOperadorDetalhado(null)}
              >
                Fechar
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingDetalhado ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : detalhadoData.mensagens && detalhadoData.mensagens.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium">Cliente</th>
                      <th className="text-left py-3 px-4 font-medium">Mensagem</th>
                      <th className="text-left py-3 px-4 font-medium">Tipo</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Data/Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalhadoData.mensagens.map((msg: any) => (
                      <tr
                        key={msg.id}
                        className="border-b border-gray-100 dark:border-slate-700 dark:border-slate-700 hover:bg-muted/50"
                      >
                        <td className="py-3 px-4">
                          <p className="font-medium">{msg.clienteNome}</p>
                        </td>
                        <td className="py-3 px-4">
                          <div className="max-w-xs">
                            <p className="text-sm truncate">{msg.content || 'Mídia'}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-xs">
                            {msg.messageType}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={getStatusColor(msg.status)}>
                            {msg.status === 'sent' && 'Enviado'}
                            {msg.status === 'delivered' && 'Entregue'}
                            {msg.status === 'read' && 'Lido'}
                            {msg.status === 'failed' && 'Falhou'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm">{formatDate(msg.timestamp)}</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>Nenhuma mensagem encontrada para este operador</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

